import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    CircularProgress,
    Autocomplete,
} from '@mui/material';
import { useState, useCallback, useEffect, useRef, SyntheticEvent } from 'react';
import { 
    IEmcInviteContactsParams, 
    tmsEmcInviteContacts, 
    IEmcContactIdentity, 
    user, 
    tmsEmcGetContactTypes,
    IEmcGetContactTypesResult,
    IEmcContactTypeItem,
    IEmcInviteResultItem,
    trackingPrint,
} from '@app/model';
import toast from 'react-hot-toast';
import { matrixUser } from '@im/matrix';
import { authService } from '@src/services/authService';
import { Log, useProxyWatch,
    IRequestResult } from '@fanfanlo';
const fileLogger = new Log(false, 'InviteContactDialog.tsx');

const isValidIdentity = (text: string) => /(^\d{11}$)|(^[a-zA-Z0-9]{32}$)/.test(text);
const is11DigitNumber = (text: string) => /^\d{11}$/.test(text);

interface InviteContactDialogProps {
    open: boolean;
    onClose: () => void;
    onInviteSent?: () => void;
}

export function InviteContactDialog({ open, onClose, onInviteSent }: InviteContactDialogProps) {
    const uiLogger = fileLogger.sub(false, 'InviteContactDialog_ui');

    const [contacts, setContacts] = useState('');
    const [remark, setRemark] = useState('');
    const [callType, setCallType] = useState<string | null>(null);
    const [availableCallTypes, setAvailableCallTypes] = useState<readonly IEmcContactTypeItem[]>([]);
    
    // Debug effect to log availableCallTypes changes
    useEffect(() => {
        console.log('availableCallTypes updated:', availableCallTypes);
    }, [availableCallTypes]);
    
    // Debug effect to log callType changes
    useEffect(() => {
        console.log('callType updated:', callType);
    }, [callType]);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTypesLoading, setIsTypesLoading] = useState(false);

    const [pendingParams, setPendingParams] = useState<Omit<IEmcInviteContactsParams, 'matrix_access_token'> | null>(null);
    const executionLock = useRef(false);
    const [accessToken] = useProxyWatch(matrixUser.userData.userInfo, 'loginResponse.access_token', matrixUser.userData.userInfo.loginResponse?.access_token);
    const [isUserLoggedIn] = useProxyWatch(user.data.sessionData, 'global_user_info', !!user.data.sessionData.global_user_info);
    const [userToken] = useProxyWatch(user, 'data.storeData.user_token', user.data.storeData.user_token);
    useEffect(() => {
        if (accessToken && pendingParams && !executionLock.current) {
            executionLock.current = true;
            const finalParams: IEmcInviteContactsParams = {
                ...pendingParams,
                matrix_access_token: accessToken,
                contacts: pendingParams?.contacts || [],
                type: pendingParams?.type || ''
            };
            executeApiCall(finalParams);
        }
    }, [accessToken, pendingParams]);

    const executeApiCall = useCallback(async (params: IEmcInviteContactsParams) => {
        if (!params.contacts || !params.type) {
            toast.error('请填写联系人和呼叫类型');
            return;
        }

        setIsLoading(true);
        
        try {
            const res = await tmsEmcInviteContacts({
                params,
                handler: { printLog: true }
            });

            console.log('Invite response:', res);

            // 处理响应结果
            if (res.err || !res.shellIsOk || !res.dataIsOk) {
                const errorMessage = res.err?.message || '邀请失败，请稍后重试';
                toast.error(errorMessage);
                return;
            }

            // 处理嵌套的响应结构
            const responseData = res.data;
            if (!responseData?.data?.results) {
                const errorMessage = responseData?.data?.msg || res.data?.msg || '未返回有效的结果';
                toast.error(errorMessage);
                return;
            }
            
            const results: IEmcInviteResultItem[] = responseData.data.results;

            // 统计成功和失败的数量
            const successResults = results.filter(r => r.result_code === 'SUCCESS');
            const infoResults = results.filter(r => r.result_code === 'INVITE_ALREADY_SENT' || r.result_code === 'ALREADY_A_CONTACT');
            const errorResults = results.filter(r => r.result_code !== 'SUCCESS' && r.result_code !== 'INVITE_ALREADY_SENT' && r.result_code !== 'ALREADY_A_CONTACT');

            if (successResults.length > 0) {
                toast.success(`成功邀请 ${successResults.length} 个联系人`);
                onInviteSent?.();
            }
            
            if (infoResults.length > 0) {
                const infoMessages = infoResults.map(r => r.msg).filter(Boolean).join('\n');
                if (infoMessages) {
                    // 使用默认的 toast 替代 toast.info
                    toast(infoMessages);
                }
            }
            
            if (errorResults.length > 0) {
                const errorMessages = errorResults.map(r => r.msg).filter(Boolean).join('\n');
                if (errorMessages) {
                    toast.error(`部分邀请失败：\n${errorMessages}`);
                }
            }
        } catch (error) {
            console.error('Error in executeApiCall:', error);
            toast.error('处理请求时发生错误，请稍后重试');
        } finally {
            setIsLoading(false);
        }
    }, [onInviteSent]);

    const fetchCallTypes = useCallback(async () => {
        const logger = uiLogger.sub(false, 'fetchCallTypes_fn');
        logger.log('Fetching call types...');
        setIsTypesLoading(true);
        const res: IRequestResult<IEmcGetContactTypesResult, any> = await tmsEmcGetContactTypes({ params: {}, handler: { printLog: true } });
        logger.log('Call types response:', JSON.stringify(res));
        setIsTypesLoading(false);

        if (res.err || !res.shellIsOk || !res.dataIsOk) {
            const errorMessage = res.err?.message || '加载呼叫类型失败。';
            toast.error(errorMessage);
            setAvailableCallTypes([]);
            logger.error('Failed to load call types:', errorMessage);
        } else {
            // 从 res.data.data.types 获取数据
            const types = res.data?.data?.types || [];
            logger.log('Setting availableCallTypes:', JSON.stringify(types));
            
            // 转换为 IEmcContactTypeItem[] 格式
            const formattedTypes = types.map(type => ({
                id: type.uuid,
                name: type.name,
                is_system: type.is_system
            }));
            
            setAvailableCallTypes(formattedTypes);
            
            // 只在 callType 为空时设置默认值
            if (formattedTypes.length > 0 && !callType) {
                const defaultType = formattedTypes[0].name;
                logger.log('Setting default call type:', defaultType);
                setCallType(defaultType);
            } else if (formattedTypes.length === 0) {
                logger.warn('No call types available');
            }
        }
    }, []); // 移除了 callType 依赖

    useEffect(() => {
        if (open) {
            fetchCallTypes();
        } else {
            setContacts('');
            setRemark('');
            setCallType(null);
            setAvailableCallTypes([]);
            setMessage('');
            setIsLoading(false);
            setPendingParams(null);
            executionLock.current = false;
        }
    }, [open, fetchCallTypes]);

    const handleSendInvitation = useCallback(async () => {
        const contactInputs = contacts.split('\n').map(c => c.trim()).filter(c => c);
        if (contactInputs.length === 0) {
            toast.error('请输入至少一个联系人。');
            return;
        }

        const contactIdentities: IEmcContactIdentity[] = [];
        for (const input of contactInputs) {
            if (isValidIdentity(input)) {
                const type = is11DigitNumber(input) ? 'mobile' : 'email';
                contactIdentities.push({ identity: input, identityType: type });
            } else {
                toast.error(`联系人格式无效: ${input}。必须是11位手机号或32位字符串。`);
                return;
            }
        }

        if (!callType || callType.trim() === '') {
            toast.error('请选择或输入一个呼叫类型。');
            return;
        }

        setIsLoading(true);

        if (!userToken) {
            const appLoggedIn = await authService.showLoginDialog();
            if (!appLoggedIn) {
                setIsLoading(false);
                return; 
            }
        }

        const currentAccessToken = matrixUser.userData.userInfo.loginResponse?.access_token;
        const params: Omit<IEmcInviteContactsParams, 'matrix_access_token'> = {
            contacts: contactIdentities,
            type: callType.trim(),
            remark: remark.trim(),
            message: message.trim(),
        };

        if (currentAccessToken) {
            executionLock.current = true;
            const apiParams: IEmcInviteContactsParams = {
                ...params,
                matrix_access_token: currentAccessToken,
                contacts: params.contacts || [],
                type: params.type || ''
            };
            executeApiCall(apiParams);
            onClose(); // 发送添加联系人请求后关闭dialog

        } else {
            toast('等待登录...');
            setPendingParams(params);
        }

    }, [contacts, remark, callType, message, executeApiCall, isUserLoggedIn]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>邀请紧急联系人</DialogTitle>
            <DialogContent>
                <Box component="form" sx={{ mt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="contacts"
                        label="联系人 (11位手机号或32位ID)"
                        type="text"
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        placeholder="13800138000"
                        value={contacts}
                        onChange={(e) => setContacts(e.target.value)}
                        disabled={isLoading}
                    />
                    {/* <TextField
                        margin="dense"
                        id="remark"
                        label="备注名 (可选)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        disabled={isLoading}
                    /> */}

                    {/* <Autocomplete
                        freeSolo
                        id="call-type-autocomplete"
                        options={availableCallTypes.map((option) => option.name)}
                        value={callType || ''}
                        onChange={(event: SyntheticEvent, newValue: string | null) => {
                            console.log('Call type changed to:', newValue);
                            setCallType(newValue);
                        }}
                        inputValue={callType || ''}
                        onInputChange={(event, newInputValue, reason) => {
                            // Only update when the user is typing, not when an option is selected
                            if (reason === 'input') {
                                console.log('Input value changed to:', newInputValue);
                                setCallType(newInputValue);
                            }
                        }}
                        disablePortal
                        disableClearable
                        forcePopupIcon={!isTypesLoading}
                        sx={{ mt: 1, mb: 1, width: '100%' }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="呼叫类型"
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isTypesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        ListboxProps={{
                            style: {
                                maxHeight: '200px',
                                overflow: 'auto',
                            },
                        }}
                    /> */}

                    {/* <TextField
                        margin="dense"
                        id="message"
                        label="邀请信息 (可选)"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="我已将您添加为我的紧急联系人，请接受邀请。"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={isLoading}
                    /> */}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} disabled={isLoading}>关闭</Button>
                <Button onClick={handleSendInvitation} variant="contained" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : '发送邀请'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}